import { useEffect, useState } from "react";
import { Box, Button, Card, Chip, Divider, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import "./details.css";
import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderDetailsRequest,
  getOrderDetailsSuccess,
} from "../../../../redux/orders/slice";
import Loading from "../../../../components/loading/loading";
import { propertyDetailsTypes, userType } from "../../../../utils/Constant";
import AppointmentDateDialog from "./appointmate-date-dialog";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

interface DetailsProps {
  orderId: string;
  onEditFees?: () => void;
  onEdit?: () => void;
  onChangeProperty?: () => void;
  orderStatus: number;
}

export default function Details({
  orderId,
  onEditFees,
  onEdit,
  onChangeProperty,
  orderStatus,
}: DetailsProps) {
  const [clientFee, setClientFee] = useState<any>();
  const [adjustmentClientFee, setAdjustmentClientFee] = useState<any[]>([]);
  const [inspectorFee, setInspectorFee] = useState<any>();
  const [adjustmentInspectorFee, setAdjustmentInspectorFee] = useState<any[]>(
    []
  );
  const [internalRequestor, setInternalRequestor] = useState<any[]>([]);
  const [internalContacts, setInternalContacts] = useState<any[]>([]);
  const [showAppointmentDate, setShowAppointmentDate] = useState(false);
  const [optionalPropertyDetails, setOptionalPropertyDetails] = useState<any[]>(
    []
  );

  const dispatch = useDispatch();

  const { currentUser } = useSelector((state: any) => state.user.user);
  const { loading, orderDetails } = useSelector((state: any) => state.orders);

  useEffect(() => {
    dispatch(getOrderDetailsSuccess({ response: null }));
    dispatch(getOrderDetailsRequest({ id: orderId }));
  }, [orderId]); // eslint-disable-line

  useEffect(() => {
    if (orderDetails?.fees) {
      const clientFee = orderDetails.fees.filter(
        (fee: any) =>
          fee.category === "ClientFee" && fee.adjustmentType === null
      );
      const clientFeeWithAdjustment = orderDetails.fees.filter(
        (fee: any) => fee.category === "ClientFee" && fee.adjustmentType
      );
      if (clientFee.length > 0) {
        setClientFee(clientFee[0]);
      }
      setAdjustmentClientFee(clientFeeWithAdjustment);

      const inspectorFee = orderDetails.fees.filter(
        (fee: any) =>
          fee.category === "InspectorFee" && fee.adjustmentType === null
      );
      const inspectorFeeWithAdjustment = orderDetails.fees.filter(
        (fee: any) => fee.category === "InspectorFee" && fee.adjustmentType
      );
      if (inspectorFee.length > 0) {
        setInspectorFee(inspectorFee[0]);
      }
      setAdjustmentInspectorFee(inspectorFeeWithAdjustment);
    }

    if (
      orderDetails?.contact?.internalContacts &&
      orderDetails?.contact?.internalContacts?.length > 0
    ) {
      const requestorUsers = orderDetails?.contact?.internalContacts.filter(
        (contact: any) => contact.isRequestor
      );
      setInternalRequestor(requestorUsers);
      const internalUsers = orderDetails?.contact?.internalContacts.filter(
        (contact: any) => !contact.isRequestor
      );
      setInternalContacts(internalUsers);
    }

    if (orderDetails?.propertyRecord) {
      const mappedPropertyDetails = propertyDetailsTypes
        .filter((type) => type.value in orderDetails?.propertyRecord)
        .map((type) => ({
          key: type.value,
          label: type.label,
          value: orderDetails?.propertyRecord[type.value],
        }))
        .filter((item) => item.value !== null);
      setOptionalPropertyDetails(mappedPropertyDetails);
    }
  }, [orderDetails]);

  const getPropertyContactPrimary = () => {
    return orderDetails?.propertyContacts?.filter(
      (contact: any) => contact.isPrimary
    );
  };

  const getPropertyContactNonPrimary = () => {
    return orderDetails?.propertyContacts?.filter(
      (contact: any) => contact.isPrimary === false
    );
  };

  const getClientTotalAmount = () => {
    let totalAmount = 0;
    totalAmount += Number(clientFee?.amount ?? 0);
    adjustmentClientFee.forEach((type: any) => {
      totalAmount += Number(type?.amount ?? 0);
    });
    return totalAmount.toFixed(2);
  };

  const getInspectorTotalAmount = () => {
    let totalAmount = 0;
    totalAmount += Number(inspectorFee?.amount ?? 0);
    adjustmentInspectorFee.forEach((type: any) => {
      totalAmount += Number(type?.amount ?? 0);
    });
    return totalAmount.toFixed(2);
  };

  const getMargin = () => {
    if (clientFee && inspectorFee) {
      return (
        ((clientFee?.amount - inspectorFee?.amount) / clientFee?.amount) *
        100
      ).toFixed(2);
    }
  };

  const getAdjustedMargin = () => {
    let clientTotalAmount = 0;
    clientTotalAmount += clientFee?.amount;
    adjustmentClientFee.forEach((type: any) => {
      clientTotalAmount += type?.amount;
    });

    let inspectorTotalAmount = 0;
    inspectorTotalAmount += inspectorFee?.amount;
    adjustmentInspectorFee.forEach((type: any) => {
      inspectorTotalAmount += type?.amount;
    });

    if (clientTotalAmount && inspectorTotalAmount) {
      return (
        ((clientTotalAmount - inspectorTotalAmount) / clientTotalAmount) *
        100
      ).toFixed(2);
    }
  };

  return (
    <>
      <Card
        className="card-layout"
        sx={{
          p: 2,
          backgroundColor: "#FAFAFA",
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
        }}
      >
        <Box className="card-header" sx={{ mb: 2 }}>
          <Typography variant="h5">Order Details</Typography>
        </Box>

        <Grid container spacing={2} sx={{ width: "100%" }}>
          {currentUser?.userType !== userType.inspector && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}
                  >
                    Client{" "}
                    <Box component="span" sx={{ color: "red" }}>
                      *
                    </Box>
                  </Typography>
                  <Typography>
                    {orderDetails?.client?.clientName || "-"}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}
                    >
                      Assign to project?
                    </Typography>
                    <InfoOutlinedIcon fontSize="small" color="primary" />
                  </Box>
                  <Typography>
                    {orderDetails?.project?.projectName || "-"}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}
                  >
                    Inspection Type{" "}
                    <Box component="span" sx={{ color: "red" }}>
                      *
                    </Box>
                  </Typography>
                  <Typography>
                    {orderDetails?.isContactInspection
                      ? "Contact"
                      : "No Contact"}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}
                  >
                    Photo Count{" "}
                    <Box component="span" sx={{ color: "red" }}>
                      *
                    </Box>
                  </Typography>
                  <Typography>{orderDetails?.photoCount || "-"}</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}
                  >
                    Sub Division
                  </Typography>
                  <Typography>
                    {orderDetails?.subDivision?.displayName || "-"}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}
                  >
                    Requestor{" "}
                    <Box component="span" sx={{ color: "red" }}>
                      *
                    </Box>
                  </Typography>
                  <Typography>
                    {orderDetails?.requester?.name || "-"}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}
                  >
                    Appointment or Unannounced?
                  </Typography>
                  <Typography>
                    {orderDetails?.inspectionScheduleType || "-"}
                  </Typography>
                </Box>
              </Grid>
            </>
          )}
          {currentUser?.userType === userType.inspector && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}
                  >
                    Inspector Type{" "}
                    <Box component="span" sx={{ color: "red" }}>
                      *
                    </Box>
                  </Typography>
                  <Typography>
                    {orderDetails?.isContactInspection
                      ? "Contact"
                      : "No Contact"}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}
                  >
                    Photo Count{" "}
                    <Box component="span" sx={{ color: "red" }}>
                      *
                    </Box>
                  </Typography>
                  <Typography>{orderDetails?.photoCount || "-"}</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}
                  >
                    Appointment or Unannounced?
                  </Typography>
                  <Typography>
                    {orderDetails?.inspectionScheduleType || "-"}
                  </Typography>
                </Box>
              </Grid>
            </>
          )}
        </Grid>

        {currentUser?.userType !== userType.inspector && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: "12px",
                fontWeight: 400,
                mb: 1.5,
                color: "text.secondary",
              }}
            >
              Who should we contact about this order?
            </Typography>
            {internalRequestor && internalRequestor.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  sx={{ fontSize: 16, color: "text.primary", mb: 0.5 }}
                >
                  Requestor
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {internalRequestor.map((contact: any) => (
                    <Chip
                      key={contact?.id ?? contact?.email}
                      sx={{
                        height: 22,
                        "& .MuiChip-label": { paddingLeft: 1, paddingRight: 1 },
                        borderColor: "primary.main",
                      }}
                      label={`${contact?.fullName} (${contact?.email})`}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
            {internalContacts && internalContacts.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  sx={{ fontSize: 16, color: "text.primary", mb: 0.5 }}
                >
                  Vision User(s)
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {internalContacts.map((contact: any) => (
                    <Chip
                      key={contact?.id ?? contact?.email}
                      sx={{
                        height: 22,
                        "& .MuiChip-label": { paddingLeft: 1, paddingRight: 1 },
                      }}
                      label={`${contact?.fullName} (${contact?.email})`}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
            {orderDetails?.contact?.externalContacts &&
              orderDetails?.contact?.externalContacts.length > 0 && (
                <Box>
                  <Typography
                    sx={{ fontSize: 16, color: "text.primary", mb: 0.5 }}
                  >
                    External Contact(s)
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {orderDetails?.contact?.externalContacts.map(
                      (contact: any) => (
                        <Chip
                          key={contact?.id ?? contact?.email}
                          sx={{
                            height: 22,
                            "& .MuiChip-label": {
                              paddingLeft: 1,
                              paddingRight: 1,
                            },
                          }}
                          label={`${contact?.fullName} (${contact?.email})`}
                          color="primary"
                          variant="outlined"
                        />
                      )
                    )}
                  </Box>
                </Box>
              )}
          </Box>
        )}
      </Card>
      <Card
        className="card-layout"
        sx={{
          p: 2,
          backgroundColor: "#FAFAFA",
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5">Property</Typography>
          {currentUser?.userType === userType.tenant &&
            onEdit &&
            orderStatus < 43 && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  startIcon={<EditOutlinedIcon />}
                  onClick={onEdit}
                  sx={{
                    color: "primary.main",
                    textTransform: "uppercase",
                    "&:hover": { backgroundColor: "rgba(255, 107, 53, 0.04)" },
                  }}
                >
                  Edit
                </Button>
                <Button
                  startIcon={<ChangeCircleOutlinedIcon />}
                  onClick={onChangeProperty ?? onEdit}
                  sx={{
                    color: "primary.main",
                    textTransform: "uppercase",
                    "&:hover": { backgroundColor: "rgba(255, 107, 53, 0.04)" },
                  }}
                >
                  Change Property
                </Button>
              </Box>
            )}
        </Box>

        <Grid container spacing={2} sx={{ width: "100%" }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
                Loan #
              </Typography>
              <Typography>
                {orderDetails?.propertyRecord?.loanNumber || "-"}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)", mb: 0.5 }}
              >
                Address
              </Typography>
              <Box>
                <Typography>
                  {orderDetails?.propertyRecord?.street || "-"}
                </Typography>
                {orderDetails?.propertyRecord?.address2 && (
                  <Typography>
                    {orderDetails.propertyRecord.address2}
                  </Typography>
                )}
                <Typography>
                  {[
                    orderDetails?.propertyRecord?.city,
                    orderDetails?.propertyRecord?.state,
                    orderDetails?.propertyRecord?.zip,
                  ]
                    .filter(Boolean)
                    .join(", ") || "-"}
                </Typography>
                {orderDetails?.propertyRecord?.country && (
                  <Typography>{orderDetails.propertyRecord.country}</Typography>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
                Property Name
              </Typography>
              <Typography>
                {orderDetails?.propertyRecord?.propertyName || "-"}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box>
          <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)", mb: 1 }}>
            Property Contact
          </Typography>
          {!orderDetails?.propertyContacts ||
          orderDetails.propertyContacts.length === 0 ? (
            <Typography color="text.secondary">-</Typography>
          ) : (
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid size={{ xs: 12, md: 6 }}>
                {getPropertyContactPrimary() &&
                  getPropertyContactPrimary().map((contact: any) => (
                    <Box key={contact?.id ?? contact?.email}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PersonOutlinedIcon fontSize="small" />
                        <Typography>{contact?.name}</Typography>
                        <Chip
                          label="Primary"
                          variant="outlined"
                          size="small"
                          sx={{
                            borderColor: "primary.main",
                            color: "primary.main",
                            backgroundColor: "white",
                          }}
                        />
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 0.5 }}
                      >
                        <MailOutlinedIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        <Typography variant="body2">
                          {contact?.email}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 0.5 }}
                      >
                        <PhoneOutlinedIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        <Typography variant="body2">
                          {contact?.phone}
                          {contact?.phoneType ? ` (${contact.phoneType})` : ""}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                {(!getPropertyContactPrimary() ||
                  getPropertyContactPrimary().length === 0) && (
                  <Typography color="text.secondary">-</Typography>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                {getPropertyContactNonPrimary() &&
                  getPropertyContactNonPrimary().map((contact: any) => (
                    <Box key={contact?.id ?? contact?.email}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PersonOutlinedIcon fontSize="small" />
                        <Typography>{contact?.name}</Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 0.5 }}
                      >
                        <MailOutlinedIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        <Typography variant="body2">
                          {contact?.email}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 0.5 }}
                      >
                        <PhoneOutlinedIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        <Typography variant="body2">
                          {contact?.phone}
                          {contact?.phoneType ? ` (${contact.phoneType})` : ""}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                {(!getPropertyContactNonPrimary() ||
                  getPropertyContactNonPrimary().length === 0) && (
                  <Typography color="text.secondary">-</Typography>
                )}
              </Grid>
            </Grid>
          )}
        </Box>

        {optionalPropertyDetails.length > 0 && (
          <Box mt={2}>
            <Typography
              sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)", mb: 1 }}
            >
              Optional Property Details
            </Typography>
            <Box display={"flex"}>
              <Typography sx={{ flex: 1 }}>
                <b>Type</b>
              </Typography>
              <Typography sx={{ flex: 3 }}>
                <b>Description</b>
              </Typography>
            </Box>
            {optionalPropertyDetails.map((details) => (
              <Box display={"flex"} mt={0.5}>
                <Typography sx={{ flex: 1 }}>{details?.label}</Typography>
                <Typography sx={{ flex: 3 }}>
                  {details?.key === "operationsAndMaintenanceRequired"
                    ? [details?.value === true ? "True" : "False"]
                    : String(details?.value)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Card>

      <Card
        className="card-layout"
        sx={{
          p: 2,
          backgroundColor: "#FAFAFA",
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Form
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
            Form to Use
          </Typography>
          <Typography>{orderDetails?.form?.displayName || "-"}</Typography>
        </Box>
      </Card>

      <Card
        className="card-layout"
        sx={{
          p: 2,
          backgroundColor: "#FAFAFA",
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Dates
        </Typography>
        <Grid container spacing={2} sx={{ width: "100%" }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            {currentUser?.userType !== userType.inspector && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
                  Start Date
                </Typography>
                <Typography>
                  {orderDetails?.startDate
                    ? dayjs(orderDetails.startDate).format("MMMM D, YYYY")
                    : "-"}
                </Typography>
              </Box>
            )}
            {currentUser?.userType !== userType.client && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
                  Inspector Due Date
                </Typography>
                <Typography>
                  {orderDetails?.inspectorDueDate
                    ? dayjs(orderDetails.inspectorDueDate).format(
                        "MMMM D, YYYY"
                      )
                    : "-"}
                </Typography>
              </Box>
            )}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
                Rush Inspection?
              </Typography>
              <Typography>{orderDetails?.rushFeeLabel || "-"}</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            {currentUser?.userType !== userType.client && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
                  Appointment Date/Estimated Completion Date
                </Typography>
                {orderDetails?.appointmentDate ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Typography>
                      {dayjs(orderDetails.appointmentDate).format(
                        "MMMM D, YYYY"
                      )}
                    </Typography>
                    {(currentUser?.userType === userType.inspector ||
                      currentUser?.userType === userType.tenant) && (
                      <Button
                        size="small"
                        startIcon={<EditOutlinedIcon />}
                        onClick={() => setShowAppointmentDate(true)}
                        sx={{
                          color: "primary.main",
                          textTransform: "uppercase",
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>
                ) : (
                  [
                    currentUser?.userType === userType.inspector ? (
                      <Button
                        startIcon={<AddOutlinedIcon />}
                        variant="text"
                        onClick={() => setShowAppointmentDate(true)}
                        sx={{
                          color: "primary.main",
                          textTransform: "uppercase",
                          p: 0,
                        }}
                      >
                        Set Appointment/Estimated Completion Date
                      </Button>
                    ) : (
                      <Typography color="text.secondary">-</Typography>
                    ),
                  ]
                )}
              </Box>
            )}
            {currentUser?.userType !== userType.inspector && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
                  Client Due Date
                </Typography>
                <Typography>
                  {orderDetails?.clientDueDate
                    ? dayjs(orderDetails.clientDueDate).format("MMMM D, YYYY")
                    : "-"}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Card>

      {currentUser?.userType === userType.tenant && (
        <Card
          className="card-layout"
          sx={{
            p: 2,
            backgroundColor: "#FAFAFA",
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h5">Fees</Typography>
            <Button
              startIcon={<EditOutlinedIcon />}
              onClick={onEditFees}
              sx={{
                color: "primary.main",
                textTransform: "uppercase",
                "&:hover": { backgroundColor: "rgba(255, 107, 53, 0.04)" },
              }}
            >
              Edit
            </Button>
          </Box>
          {clientFee ? (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", md: "row" },
                width: "100%",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 140,
                  p: 2,
                  backgroundColor: "#FAFAFA",
                  border: "1px solid #C4C4C4",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1.5 }}>
                  Client
                </Typography>
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.7)" }}
                  >
                    Billed
                  </Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    ${Number(clientFee?.amount ?? 0).toFixed(2)}
                  </Typography>
                </Box>
                {adjustmentClientFee.length > 0 && (
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography
                      sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.7)" }}
                    >
                      Adjustments
                    </Typography>
                    <Typography sx={{ fontSize: 14 }}>
                      $
                      {adjustmentClientFee
                        .reduce(
                          (s: number, a: any) => s + (Number(a?.amount) || 0),
                          0
                        )
                        .toFixed(2)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    Adjusted Total
                  </Typography>
                  <Typography fontWeight={600} sx={{ fontSize: 14 }}>
                    ${getClientTotalAmount()}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 140,
                  p: 2,
                  backgroundColor: "#FAFAFA",
                  border: "1px solid #C4C4C4",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1.5 }}>
                  Inspector
                </Typography>
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.7)" }}
                  >
                    Cost
                  </Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    ${Number(inspectorFee?.amount ?? 0).toFixed(2)}
                  </Typography>
                </Box>
                {adjustmentInspectorFee.length > 0 && (
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography
                      sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.7)" }}
                    >
                      Adjustments
                    </Typography>
                    <Typography sx={{ fontSize: 14 }}>
                      $
                      {adjustmentInspectorFee
                        .reduce(
                          (s: number, a: any) => s + (Number(a?.amount) || 0),
                          0
                        )
                        .toFixed(2)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    Adjusted Total
                  </Typography>
                  <Typography fontWeight={600} sx={{ fontSize: 14 }}>
                    ${getInspectorTotalAmount()}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 140,
                  p: 2,
                  backgroundColor: "#FAFAFA",
                  border: "1px solid #C4C4C4",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1.5 }}>
                  Spectrum
                </Typography>
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.7)" }}
                  >
                    Margin
                  </Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    {getMargin() ?? "-"}%
                  </Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    Adjusted Margin
                  </Typography>
                  <Typography fontWeight={600} sx={{ fontSize: 14 }}>
                    {getAdjustedMargin() ?? "-"}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">-</Typography>
          )}
        </Card>
      )}

      {currentUser?.userType === userType.client && (
        <Card
          className="card-layout"
          sx={{
            p: 2,
            backgroundColor: "#FAFAFA",
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h5">Fees</Typography>
          </Box>
          {clientFee ? (
            <Box
              sx={{
                display: "flex",
              }}
            >
              <Box
                sx={{
                  width: "30%",
                  minHeight: 140,
                  p: 2,
                  backgroundColor: "#FAFAFA",
                  border: "1px solid #C4C4C4",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1.5 }}>
                  Client
                </Typography>
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.7)" }}
                  >
                    Billed
                  </Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    ${Number(clientFee?.amount ?? 0).toFixed(2)}
                  </Typography>
                </Box>
                {adjustmentClientFee.length > 0 && (
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography
                      sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.7)" }}
                    >
                      Adjustments
                    </Typography>
                    <Typography sx={{ fontSize: 14 }}>
                      $
                      {adjustmentClientFee
                        .reduce(
                          (s: number, a: any) => s + (Number(a?.amount) || 0),
                          0
                        )
                        .toFixed(2)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    Adjusted Total
                  </Typography>
                  <Typography fontWeight={600} sx={{ fontSize: 14 }}>
                    ${getClientTotalAmount()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">-</Typography>
          )}
        </Card>
      )}

      {currentUser?.userType === userType.inspector && (
        <Card
          className="card-layout"
          sx={{
            p: 2,
            backgroundColor: "#FAFAFA",
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h5">Fees</Typography>
          </Box>
          {inspectorFee ? (
            <Box
              sx={{
                display: "flex",
              }}
            >
              <Box
                sx={{
                  width: "30%",
                  minHeight: 140,
                  p: 2,
                  backgroundColor: "#FAFAFA",
                  border: "1px solid #C4C4C4",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1.5 }}>
                  Inspector
                </Typography>
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.7)" }}
                  >
                    Cost
                  </Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    ${Number(inspectorFee?.amount ?? 0).toFixed(2)}
                  </Typography>
                </Box>
                {adjustmentInspectorFee.length > 0 && (
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography
                      sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.7)" }}
                    >
                      Adjustments
                    </Typography>
                    <Typography sx={{ fontSize: 14 }}>
                      $
                      {adjustmentInspectorFee
                        .reduce(
                          (s: number, a: any) => s + (Number(a?.amount) || 0),
                          0
                        )
                        .toFixed(2)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    Adjusted Total
                  </Typography>
                  <Typography fontWeight={600} sx={{ fontSize: 14 }}>
                    ${getInspectorTotalAmount()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary">-</Typography>
          )}
        </Card>
      )}

      <Card
        className="card-layout"
        sx={{
          p: 2,
          backgroundColor: "#FAFAFA",
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Attachments
        </Typography>
        {orderDetails?.attachments && orderDetails?.attachments?.length > 0 ? (
          currentUser?.userType !== userType.inspector ? (
            <Box>
              <Grid container sx={{ width: "100%", pb: 0.5, mb: 0.5 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "rgba(0, 0, 0, 0.6)",
                      fontWeight: 600,
                    }}
                  >
                    Document
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "rgba(0, 0, 0, 0.6)",
                      fontWeight: 600,
                    }}
                  >
                    Send to Inspector?
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "rgba(0, 0, 0, 0.6)",
                      fontWeight: 600,
                    }}
                  >
                    Include with Results?
                  </Typography>
                </Grid>
              </Grid>
              {orderDetails.attachments.map((document: any) => (
                <Grid
                  container
                  key={document?.id}
                  sx={{ width: "100%", py: 0.5 }}
                >
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography
                      component="a"
                      href={document?.attachmentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary"
                      sx={{ textDecoration: "underline" }}
                    >
                      {document?.attachmentName}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    {document?.sendToInspector ? (
                      <Typography color="success.main">✓</Typography>
                    ) : (
                      <Typography color="error">✕</Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    {document?.includeWithResult ? (
                      <Typography color="success.main">✓</Typography>
                    ) : (
                      <Typography color="error">✕</Typography>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {orderDetails.attachments.map((document: any) => (
                <Typography
                  key={document?.id}
                  component="a"
                  href={document?.attachmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  sx={{ textDecoration: "underline" }}
                >
                  {document?.attachmentName}
                </Typography>
              ))}
            </Box>
          )
        ) : (
          <Typography color="text.secondary">-</Typography>
        )}
      </Card>

      {currentUser?.userType !== userType.inspector && (
        <Card
          className="card-layout"
          sx={{
            p: 2,
            backgroundColor: "#FAFAFA",
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
          }}
        >
          <Typography variant="h5" sx={{ mb: 2 }}>
            Optional Order Details
          </Typography>
          {(() => {
            const optionalRows = [
              { type: "Obligor", value: orderDetails?.obligor },
              { type: "Obligation", value: orderDetails?.obligation },
              { type: "Project ID", value: orderDetails?.projectReferenceId },
              { type: "Option 1", value: orderDetails?.option1 },
              { type: "Option 2", value: orderDetails?.option2 },
            ].filter((r) => r.value != null && String(r.value).trim() !== "");
            if (optionalRows.length === 0) {
              return <Typography color="text.secondary">-</Typography>;
            }
            return (
              <Grid container spacing={2} sx={{ width: "100%" }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      mb: 0.5,
                      color: "rgba(0, 0, 0, 0.6)",
                    }}
                  >
                    Type
                  </Typography>
                  {optionalRows.map((r) => (
                    <Typography key={r.type} sx={{ mb: 1.5 }}>
                      {r.type}
                    </Typography>
                  ))}
                </Grid>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      mb: 0.5,
                      color: "rgba(0, 0, 0, 0.6)",
                    }}
                  >
                    Description
                  </Typography>
                  {optionalRows.map((r) => (
                    <Typography key={r.type} sx={{ mb: 1.5 }}>
                      {r.value}
                    </Typography>
                  ))}
                </Grid>
              </Grid>
            );
          })()}
        </Card>
      )}

      <Card
        className="card-layout"
        sx={{
          p: 2,
          backgroundColor: "#FAFAFA",
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Order Notes
        </Typography>
        {orderDetails?.notes && orderDetails?.notes?.length > 0 ? (
          <Box>
            <Grid
              container
              sx={{
                width: "100%",
                borderBottom: "1px solid #E0E0E0",
                pb: 0.5,
                mb: 0.5,
              }}
            >
              <Grid
                size={{
                  xs: 12,
                  md: currentUser?.userType !== userType.inspector ? 10 : 12,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "rgba(0, 0, 0, 0.6)",
                    fontWeight: 600,
                  }}
                >
                  Note
                </Typography>
              </Grid>
              {currentUser?.userType !== userType.inspector && (
                <Grid size={{ xs: 12, md: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "rgba(0, 0, 0, 0.6)",
                      fontWeight: 600,
                    }}
                  >
                    Inspector Viewable?
                  </Typography>
                </Grid>
              )}
            </Grid>
            {orderDetails.notes.map((note: any, index: number) => (
              <Grid
                container
                key={note?.id ?? index}
                sx={{ width: "100%", py: 0.5 }}
              >
                <Grid
                  size={{
                    xs: 12,
                    md: currentUser?.userType !== userType.inspector ? 10 : 12,
                  }}
                >
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>
                    {note?.note || "-"}
                  </Typography>
                </Grid>
                {currentUser?.userType !== userType.inspector && (
                  <Grid size={{ xs: 12, md: 2 }}>
                    {note?.sendToInspector ? (
                      <Typography color="success.main">✓</Typography>
                    ) : (
                      <Typography color="error">✕</Typography>
                    )}
                  </Grid>
                )}
              </Grid>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary">-</Typography>
        )}
      </Card>
      {showAppointmentDate && (
        <AppointmentDateDialog
          orderId={orderId}
          isEdit={!!orderDetails?.appointmentDate}
          showDialog={showAppointmentDate}
          closeDialog={() => setShowAppointmentDate(false)}
        />
      )}
      {loading && <Loading />}
    </>
  );
}
